import { useContext, useEffect, useState } from 'react';
import { DataTable, DataTableColumn } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { QuipliLink } from '../../../components/link/quipli-link';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { TenantContext } from '../../../context/tenant-context';
import classNames from 'classnames';
import { useAdmin } from '../../../hooks/use-admin-context';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { CreateAdminModal } from '../components/create-admin-modal';
import { DeleteAdminModal } from '../components/delete-admin-modal';
import { useAdminOperations } from '../hooks/use-admin-operations';

export const AdminUsersList = () => {
  const adminLogged = useAdmin();

  const { listUsers, isListing } = useAdminOperations();
  const [admins, setAdmins] = useState<Admin[]>([]);
  // State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [deleteModalAdmin, setDeleteModalAdmin] = useState<Admin | null>(null);

  const columns: DataTableColumn[] = [
    { key: 'name', label: 'Name', showRule: 'OnlyDesktop' },
    { key: 'userName', label: 'User name', showRule: 'OnlyDesktop' },
    { key: 'email', label: 'Email', showRule: 'OnlyDesktop' },
    { key: 'superAdmin', label: 'Super admin', showRule: 'OnlyDesktop' },
    { key: 'actions', label: '', fixedAsLast: true, showRule: 'OnlyDesktop' },
  ];

  const buildDataRow = (admin: Admin) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium" className="text-quipli-black-800">
            {admin.adminIsSuperAdmin ? (
              admin.adminFullName
            ) : (
              <QuipliLink
                to={
                  admin.adminId === adminLogged.id ? `admins/${admin.adminId}/permissions` : `admins/${admin.adminId}`
                }
                className={classNames('underline text-[var(--quipli-black-800)]')}
              >
                {admin.adminFullName}
              </QuipliLink>
            )}
          </Typography>
          <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
            {admin.adminEmail}
          </Typography>
        </>
      ),
      mobileHeaderAction: <Actions adminUser={admin} deleteUser={() => setDeleteModalAdmin(admin)} />,
      fields: {
        name: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {admin.adminFullName}
          </Typography>
        ),

        userName: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {admin.adminUsername}
          </Typography>
        ),
        email: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {admin.adminEmail}
          </Typography>
        ),
        superAdmin: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {admin.adminIsSuperAdmin ? 'Yes' : 'No'}
          </Typography>
        ),
        actions: <Actions adminUser={admin} deleteUser={() => setDeleteModalAdmin(admin)} />,
      },
      redirectTo: admin.adminId === adminLogged.id ? `admins/${admin.adminId}/permissions` : `admins/${admin.adminId}`,
    };
  };

  useEffect(() => {
    listUsers(setAdmins);
  }, []);

  return (
    <>
      <div className="admin-users-list">
        <div className="container-v2 relative p-2">
          <div className="pb-5 flex justify-between items-center">
            <Typography type="display-sm" weight="medium">
              Manage Admin Users
            </Typography>
            <div className="flex flex-row gap-2">
              <Button
                className="button-yellow !border-0 h-[46px]"
                data-cy="add-admin"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Typography
                  type="text-md"
                  weight="semi-bold"
                  className="flex justify-center justify-items-center gap-1 text-grey-700"
                >
                  <Icon icon="Plus" stroke="var(--grey-700)" />
                  Add User
                </Typography>
              </Button>
            </div>
          </div>

          <DataTable
            isLoading={isListing}
            columnNames={columns}
            data={admins.map(buildDataRow)}
            cypressPrevious="previous_renters_page"
            cypressNext="next_renters_page"
          />
        </div>
      </div>

      <CreateAdminModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          listUsers(setAdmins);
          setIsCreateModalOpen(false);
        }}
      />

      <DeleteAdminModal
        admin={deleteModalAdmin}
        isOpen={!!deleteModalAdmin}
        onClose={() => setDeleteModalAdmin(null)}
        onSuccess={() => {
          listUsers(setAdmins);
          setDeleteModalAdmin(null);
        }}
      />
    </>
  );
};

const Actions = ({ adminUser, deleteUser }: { adminUser: Admin; deleteUser: () => void }) => {
  const { contextPath } = useContext(TenantContext);
  const adminLogged = useAdmin();

  return (
    <div>
      {!adminUser.adminIsSuperAdmin && (
        <Dropdown
          placement="left-end"
          trigger={() => (
            <Button className="button-tertiary" data-cy={`admin-menu`}>
              <Icon icon="DotsVertical" />
            </Button>
          )}
        >
          {adminLogged.isSuperAdmin && (
            <div
              onClick={() => {
                const url = `${contextPath}manager/admin/login_as/${adminUser.adminId}`;
                window.open(url, '_blank')?.focus();
              }}
              data-cy="login-as-admin"
            >
              <div className="flex gap-2">
                <Icon icon="LogIn04" />
                <Typography type="text-sm" className="text-quipli-black-800">
                  Login as Admin
                </Typography>
              </div>
            </div>
          )}

          {adminUser.adminId !== adminLogged.id && (
            <div className="flex gap-2" onClick={deleteUser} data-cy="delete-admin">
              <Icon icon="Trash" stroke="var(--grey-500)" />
              <Typography type="text-sm" className="text-quipli-black-800">
                Remove User
              </Typography>
            </div>
          )}
        </Dropdown>
      )}
    </div>
  );
};
